import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../contexts/PermissionContext';
import { useThemeMode } from '../../ThemeContext';
import { APP_URLS } from '../../helpers/url_helper';
import { dataAPI } from '../../helpers/api_service';
import { formatDate } from '../../helpers/UtilsFunctions';
import {
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import AddSiteModal from './AddSiteModal';
import { AlertDialog } from '../Common';
import '../../styles/loading-animations.css';

const PAGE_SIZES = [5, 10, 25, 50];
const THEME_GREEN = '#009391';

const SitesList = () => {
  const navigate = useNavigate();
  const { mode } = useThemeMode();
  const {
    permissions,
    hasReadPermission,
    hasAddPermission,
    hasEditPermission,
    hasDeletePermission,
  } = usePermissions();
  const canReadSites = hasReadPermission('site');
  const canAddSite = hasAddPermission('site');
  const canEditSite = hasEditPermission('site');
  const canDeleteSite = hasDeletePermission('site');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sites, setSites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ isOpen: false, site: null });
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchSites = async () => {
    try {
      setIsLoading(true);
      setError('');
      const response = await dataAPI.getSitesList({ showLoading: false });
      setSites(Array.isArray(response?.data) ? response.data : []);
    } catch (apiError) {
      setSites([]);
      setError(apiError?.message || 'Failed to load sites.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (permissions === null) return;
    if (!canReadSites) {
      setIsLoading(false);
      return;
    }
    fetchSites();
  }, [canReadSites, permissions]);

  useEffect(() => {
    if (permissions && !canReadSites) {
      navigate('/dashboard', { replace: true });
    }
  }, [permissions, canReadSites, navigate]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return sites;
    return sites.filter((item) =>
      [
        item.site_name,
        item.site_address,
        item.status,
        item.created_by_username,
      ]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  }, [sites, search]);

  const startIndex = page * pageSize;
  const pageItems = filtered.slice(startIndex, startIndex + pageSize);

  useEffect(() => {
    setPage(0);
  }, [search, pageSize]);

  const handleAddSite = () => {
    setEditingSite(null);
    setIsAddModalOpen(true);
  };

  const handleViewSite = (site) => {
    navigate(`${APP_URLS.SITES}/${site.id}`, { state: { site } });
  };

  const handleEditSite = (site) => {
    setEditingSite(site);
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
    setEditingSite(null);
  };

  const handleSiteSaved = (savedSite, isEdit = false) => {
    if (!savedSite?.id) {
      fetchSites();
      return;
    }
    if (isEdit) {
      setSites((prev) =>
        prev.map((item) => (item.id === savedSite.id ? { ...item, ...savedSite } : item))
      );
    } else {
      setSites((prev) => [savedSite, ...prev]);
    }
    setEditingSite(null);
  };

  const handleDeleteClick = (site) => {
    setDeleteDialog({ isOpen: true, site });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, site: null });
  };

  const handleDeleteConfirm = async () => {
    const site = deleteDialog.site;
    if (!site?.id || isDeleting) return;

    setIsDeleting(true);
    try {
      const response = await dataAPI.deleteSite(site.id, { showLoading: false });
      if (response?.status === 'success') {
        setSites((prev) => prev.filter((item) => item.id !== site.id));
        handleDeleteCancel();
      } else {
        alert(response?.message || 'Failed to delete site.');
      }
    } catch (apiError) {
      alert(apiError?.message || 'Failed to delete site.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (permissions && !canReadSites) {
    return null;
  }

  return (
    <div
      style={{
        padding: '24px',
        minHeight: '100vh',
        background: mode === 'dark' ? '#0f0f0f' : '#ffffff',
        color: mode === 'dark' ? '#fff' : '#111827',
        fontFamily: 'Urbanist, Arial, sans-serif',
      }}
    >
      {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 'calc(100vh - 220px)',
          }}
        >
          <img
            src={AppIconBlue}
            alt="Loading"
            className="pulse-animation-icon"
            style={{ width: 46, height: 46 }}
          />
        </div>
      ) : error ? (
        <div
          style={{
            border: `1px solid ${mode === 'dark' ? '#7f1d1d' : '#fecaca'}`,
            borderRadius: '12px',
            padding: '16px',
            color: mode === 'dark' ? '#fecaca' : '#b91c1c',
          }}
        >
          {error}
        </div>
      ) : sites.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: 'calc(100vh - 240px)',
            textAlign: 'center',
          }}
        >
          <h2 style={{ margin: 0, marginBottom: '10px', color: mode === 'dark' ? '#ffffff' : '#1f2937' }}>
            No Sites Yet
          </h2>
          <p style={{ margin: 0, marginBottom: '20px', color: mode === 'dark' ? '#d1d5db' : '#4b5563' }}>
            {canAddSite ? 'Start by adding your first site.' : 'No sites available.'}
          </p>
          {canAddSite && (
            <button
              type="button"
              onClick={handleAddSite}
              style={{
                border: 'none',
                borderRadius: '8px',
                padding: '10px 16px',
                background: '#009391',
                color: '#fff',
                cursor: 'pointer',
                fontWeight: 600,
              }}
            >
              Add Site
            </button>
          )}
        </div>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              flexWrap: 'wrap',
            }}
          >
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by site name, address, status..."
              style={{
                width: '100%',
                maxWidth: '420px',
                borderRadius: '8px',
                border: '1.5px solid #009391',
                padding: '10px 12px',
                background: mode === 'dark' ? '#111111' : '#ffffff',
                color: mode === 'dark' ? '#ffffff' : '#111111',
                outline: 'none',
                boxShadow: '0 0 0 1px rgba(0, 147, 145, 0.15)',
              }}
            />
            {canAddSite && (
              <button
                type="button"
                onClick={handleAddSite}
                style={{
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  background: '#009391',
                  color: '#fff',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                Add Site
              </button>
            )}
          </div>

          <Paper
            sx={{
              border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e5e7eb'}`,
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: 'none',
              backgroundColor: mode === 'dark' ? '#0f0f0f' : '#ffffff',
            }}
          >
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={thStyle(mode)}>Site Name</TableCell>
                    <TableCell sx={thStyle(mode)}>Address</TableCell>
                    <TableCell sx={thStyle(mode)}>Status</TableCell>
                    <TableCell sx={thStyle(mode)}>Created By</TableCell>
                    <TableCell sx={thStyle(mode)}>Created At</TableCell>
                    <TableCell sx={{ ...thStyle(mode), textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageItems.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={6}
                        sx={{
                          padding: '24px',
                          textAlign: 'center',
                          color: mode === 'dark' ? '#9ca3af' : '#6b7280',
                        }}
                      >
                        No sites found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    pageItems.map((item) => (
                      <TableRow
                        key={item.id}
                        sx={{
                          '& td': {
                            borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                          },
                        }}
                      >
                        <TableCell sx={tdStyle}>{item.site_name || '-'}</TableCell>
                        <TableCell sx={tdStyle}>{item.site_address || '-'}</TableCell>
                        <TableCell sx={tdStyle}>
                          <span style={getStatusBadgeStyle(item.status, mode)}>
                            {formatStatusLabel(item.status)}
                          </span>
                        </TableCell>
                        <TableCell sx={tdStyle}>{item.created_by_username || '-'}</TableCell>
                        <TableCell sx={tdStyle}>
                          {item.created_at ? formatDate(item.created_at) : '-'}
                        </TableCell>
                        <TableCell sx={{ ...tdStyle, textAlign: 'center' }}>
                          <div style={{ display: 'inline-flex', gap: '4px' }}>
                            {canReadSites && (
                              <Tooltip title="View">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewSite(item)}
                                  sx={{ color: THEME_GREEN }}
                                >
                                  <VisibilityOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canEditSite && (
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditSite(item)}
                                  sx={{ color: THEME_GREEN }}
                                >
                                  <EditOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canDeleteSite && (
                              <Tooltip title="Delete">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteClick(item)}
                                  sx={{ color: '#ef4444' }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(event) => {
                setPageSize(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={PAGE_SIZES}
              sx={{
                color: mode === 'dark' ? '#d0d5dd' : '#344054',
                borderTop: `1px solid ${mode === 'dark' ? '#1f2937' : '#f3f4f6'}`,
                backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f8f9fa',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  margin: 0,
                },
              }}
            />
          </Paper>
        </>
      )}

      <AddSiteModal
        key={editingSite?.id ? `edit-${editingSite.id}` : 'create'}
        isOpen={isAddModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleSiteSaved}
        mode={editingSite ? 'edit' : 'create'}
        site={editingSite}
      />

      <AlertDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        type="delete"
        title="Delete Site"
        message="Are you sure you want to delete this site? This action cannot be undone."
        itemName={deleteDialog.site?.site_name || ''}
        confirmText={isDeleting ? 'Deleting...' : 'Delete'}
        cancelText="Cancel"
      />
    </div>
  );
};

const thStyle = (mode) => ({
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
  fontWeight: 700,
  backgroundColor: mode === 'dark' ? '#111111' : '#f9fafb',
  color: mode === 'dark' ? '#ffffff' : '#111827',
});

const tdStyle = {
  textAlign: 'left',
  padding: '10px 12px',
  fontSize: '13px',
};

const formatStatusLabel = (value) => {
  if (!value) return '-';
  const normalized = String(value).toLowerCase();
  if (normalized === 'active') return 'Active';
  if (normalized === 'inactive' || normalized === 'deactive') return 'Inactive';
  return String(value).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const getStatusBadgeStyle = (value, mode) => {
  const base = {
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '999px',
    fontSize: '12px',
    fontWeight: 600,
    border: '1px solid transparent',
  };
  const normalized = String(value || '').toLowerCase();
  if (normalized === 'active') {
    return {
      ...base,
      background: mode === 'dark' ? 'rgba(16,185,129,0.15)' : '#ecfdf5',
      color: '#059669',
      borderColor: mode === 'dark' ? 'rgba(16,185,129,0.4)' : '#a7f3d0',
    };
  }
  if (normalized === 'inactive' || normalized === 'deactive') {
    return {
      ...base,
      background: mode === 'dark' ? 'rgba(239,68,68,0.15)' : '#fef2f2',
      color: '#dc2626',
      borderColor: mode === 'dark' ? 'rgba(239,68,68,0.4)' : '#fecaca',
    };
  }
  return {
    ...base,
    background: mode === 'dark' ? '#1f2937' : '#f3f4f6',
    color: mode === 'dark' ? '#e5e7eb' : '#374151',
    borderColor: mode === 'dark' ? '#374151' : '#e5e7eb',
  };
};

export default SitesList;
